import { Footer } from '@/app/components/Footer'
import { Header } from '@/app/modules/shared/components/Header'
import { useLanguageSelection } from '@/app/modules/shared/providers/LanguageSelectionProvider'
import { simple } from 'instantsearch.js/es/lib/stateMappings'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import singletonRouter from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { InstantSearch, UseSortByProps } from 'react-instantsearch'
import { createInstantSearchRouterNext } from 'react-instantsearch-router-nextjs'
import { useMediaQuery } from 'usehooks-ts'
import { useTheme } from '../../shared/hooks/useTheme'
import { CoursesPageHeader } from '../components/CoursesPageHeader'
import { CustomCurrentRefinements } from '../components/CustomCurrentRefinements'
import { InfiniteCourseHits } from '../components/InfiniteCourseHits'
import { RefinementsMenu } from '../components/RefinementsMenu'
import { SearchFacetsDrawer } from '../components/SearchFacetsDrawer'
import { SearchRefinements } from '../components/SearchRefinements'
import { SearchSortBy } from '../components/SearchSortBy'
import { SidebarRefinements } from '../components/SidebarRefinements'
import { getSearchClient } from '../lib/search-client'

type ScopedApiKeys = {
  courses: string
}

type ExternalSearchCoursesPageProps = {
  scopedApiKeys: ScopedApiKeys
}

export function ExternalSearchCoursesPage({
  scopedApiKeys,
}: ExternalSearchCoursesPageProps) {
  const t = useTranslations('SearchCoursesPage')
  const [isDrawerOpen, setDrawerOpen] = useState(false)
  const {
    theme: {
      screens: { md },
    },
  } = useTheme()

  const isDesktop = useMediaQuery(`(min-width: ${md})`, {
    defaultValue: false,
  })

  const { currentLocale } = useLanguageSelection()

  useEffect(() => {
    if (isDesktop && isDrawerOpen) {
      setDrawerOpen(false)
    }
  }, [isDesktop, isDrawerOpen, setDrawerOpen])

  const DEFAULT_SORT_BY = {
    label: t('sort_most_relevant'),
    value: 'courses/sort/quality_score:desc,education_provider.hash:asc', // Default sort by quality score
  }

  const COURSE_SORT_OPTIONS: UseSortByProps['items'] = [
    DEFAULT_SORT_BY,

    { label: t('sort_most_recent'), value: 'courses/sort/updated_at:desc' },
    {
      label: t('sort_price_low_to_high'),
      value: 'courses/sort/min_price.amount:asc',
    },
    {
      label: t('sort_price_high_to_low'),
      value: 'courses/sort/min_price.amount:desc',
    },
    {
      label: t('sort_alphabetical_a_to_z'),
      value: `courses/sort/name_${currentLocale}:asc`,
    },
    {
      label: t('sort_alphabetical_z_to_a'),
      value: `courses/sort/name_${currentLocale}:desc`,
    },
  ]

  const memoizedSearchClient = useMemo(() => {
    try {
      return getSearchClient('', {
        collectionSpecificSearchParameters: {
          courses: {
            query_by: `name_${currentLocale},learning_description_${currentLocale}`,
            'x-typesense-api-key': scopedApiKeys.courses,
          },
        },
      })
    } catch {
      return null
    }
  }, [currentLocale, scopedApiKeys])

  /**
   * Exclude pagination state from URL routing to prevent users from landing on a
   * deep page via URL without the ability to navigate back. Since this page uses
   * infinite scroll (load more only), there's no UI affordance to view previous
   * results when starting from an arbitrary page number.
   */
  const stateMappingWithoutPaginationSync = {
    stateToRoute(uiState) {
      const encoded = simple().stateToRoute(uiState)

      if (encoded?.courses) {
        const { page: _page, ...coursesWithoutPage } = encoded.courses

        return {
          ...encoded,
          courses: coursesWithoutPage,
        }
      }

      return encoded
    },
    routeToState(routeState) {
      const parsed = simple().routeToState(routeState)

      if (parsed?.courses) {
        const { page: _page, ...coursesWithoutPage } = parsed.courses

        return {
          ...parsed,
          courses: {
            ...coursesWithoutPage,
            sortBy: routeState?.courses?.sortBy ?? DEFAULT_SORT_BY.value,
          },
        }
      }

      return {
        ...parsed,
        courses: {
          ...parsed.courses,
          sortBy: routeState?.courses?.sortBy ?? DEFAULT_SORT_BY.value,
        },
      }
    },
  }

  if (!memoizedSearchClient) {
    return null
  }

  return (
    <InstantSearch
      indexName="courses"
      searchClient={memoizedSearchClient}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
      numberLocale={currentLocale}
      stalledSearchDelay={500}
      routing={{
        router: createInstantSearchRouterNext({
          singletonRouter,
          routerOptions: { cleanUrlOnDispose: false },
        }),
        stateMapping: stateMappingWithoutPaginationSync,
      }}
    >
      <Head>
        <title>{t('page_title')}</title>
      </Head>

      <Header useSearchIndex />

      <main className="flex flex-col gap-4 md:gap-4 lg:gap-6">
        <CoursesPageHeader onFilterButtonClick={() => setDrawerOpen(true)} />
        <nav className="container hidden md:block lg:hidden">
          <RefinementsMenu />
        </nav>
        <div className="container grid items-start pb-8 md:grid-cols-12 md:gap-x-8">
          <aside className="hidden md:col-span-2 md:justify-between lg:block">
            <SidebarRefinements />
          </aside>
          <div className="col-span-full flex flex-col justify-stretch gap-4 lg:col-span-10">
            <nav className="flex w-full items-center gap-4">
              <CustomCurrentRefinements className="hidden flex-wrap md:flex" />
              <SearchSortBy
                className="ml-auto hidden shrink-0 md:flex"
                items={COURSE_SORT_OPTIONS}
              />
            </nav>

            <InfiniteCourseHits className="grid grid-flow-row-dense grid-cols-1 gap-4 md:mt-2 md:grid-cols-2 lg:grid-cols-3" />
          </div>
        </div>
      </main>

      <SearchFacetsDrawer
        className="md:hidden"
        variant="temporary"
        open={isDrawerOpen}
        onClose={() => {
          setDrawerOpen(false)
        }}
      >
        <SearchRefinements />
      </SearchFacetsDrawer>

      <Footer />
    </InstantSearch>
  )
}
